import React from 'react';
import { BillSummary } from '@enview/interface/types/bills/BillSummary';
import { Table } from '@tanstack/react-table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAnglesLeft,
  faAngleLeft,
  faAngleRight,
  faAnglesRight,
} from '@fortawesome/free-solid-svg-icons';
import './Tables.scss';

interface PaginationBarProps {
  table: Table<BillSummary>;
  results:
    | {
        data: BillSummary[];
        count: number;
      }
    | undefined;
  pagination: {
    pageIndex: number;
    pageSize: any;
  };
}

export const PaginationBar: React.FC<PaginationBarProps> = ({
  table,
  results,
  pagination,
}) => {
  const total = results?.count ?? 0;
  const pageSize = pagination.pageSize || 10;
  const rawPageCount = total ? Math.ceil(total / pageSize) : 0;

  const safePageIndex = Math.min(
    pagination.pageIndex ?? 0,
    Math.max(rawPageCount - 1, 0),
  );

  // Compute range
  const start = total ? safePageIndex * pageSize + 1 : 0;
  const end = total ? Math.min((safePageIndex + 1) * pageSize, total) : 0;
  const rangeLabel = total === 0 ? '0 of 0' : `${start}-${end} of ${total}`;

  const canPrev = safePageIndex > 0;
  const canNext = rawPageCount > 0 && safePageIndex < rawPageCount - 1;

  return (
    <div className="table-pagination">
      <div className="rows-per-page">
        <label>Rows per page:</label>
        <select
          onChange={(e) => table.setPageSize(Number(e.target.value))}
          value={pageSize}
        >
          {[10, 20, 50].map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      <div className="page-range">{rangeLabel}</div>

      <div className="pager">
        <button
          aria-label="First page"
          disabled={!canPrev}
          onClick={() => table.setPageIndex(0)}
        >
          <FontAwesomeIcon icon={faAnglesLeft} />
        </button>

        <button
          aria-label="Previous page"
          disabled={!canPrev}
          onClick={() => table.setPageIndex(safePageIndex - 1)}
        >
          <FontAwesomeIcon icon={faAngleLeft} />
        </button>

        <button
          aria-label="Next page"
          disabled={!canNext}
          onClick={() => table.setPageIndex(safePageIndex + 1)}
        >
          <FontAwesomeIcon icon={faAngleRight} />
        </button>

        <button
          aria-label="Last page"
          disabled={!canNext}
          onClick={() => table.setPageIndex(Math.max(rawPageCount - 1, 0))}
        >
          <FontAwesomeIcon icon={faAnglesRight} />
        </button>
      </div>
    </div>
  );
};

export default PaginationBar;
